import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import { classNames } from "./Utils/classNames.ts";

export type ActionTone = "primary" | "neutral" | "danger";

export type SplitAction = {
  label: string;
  onClick: () => void;
  tone?: ActionTone;
  disabledReason?: string;
};

const MAIN_BUTTON_TONES: Record<ActionTone, string> = {
  primary: "bg-primary text-white enabled:hover:brightness-110",
  neutral:
    "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 enabled:hover:bg-gray-50",
  danger: "bg-red-600 text-white enabled:hover:bg-red-700",
};

const CHEVRON_BUTTON_TONES: Record<ActionTone, string> = {
  primary:
    "bg-primary text-white border-l border-white/30 hover:brightness-110",
  neutral:
    "bg-white text-gray-500 ring-1 ring-inset ring-gray-300 -ml-px hover:bg-gray-50",
  danger: "bg-red-600 text-white border-l border-white/30 hover:bg-red-700",
};

const SIZES = {
  sm: { main: "px-3 py-1.5 text-sm", chevron: "px-2" },
  md: { main: "px-4 py-2 text-sm", chevron: "px-2.5" },
};

const MENU_ITEM_TONES: Record<ActionTone, string> = {
  primary: "text-gray-700",
  neutral: "text-gray-700",
  danger: "text-red-600",
};

export function SplitActionButton({
  mainAction,
  otherActions = [],
  disabled = false,
  size = "sm",
}: {
  mainAction: SplitAction;
  otherActions?: SplitAction[];
  disabled?: boolean;
  size?: keyof typeof SIZES;
}) {
  const tone = mainAction.disabledReason
    ? "neutral"
    : (mainAction.tone ?? "primary");
  const hasOtherActions = otherActions.length > 0;

  return (
    <div className="inline-flex rounded-lg shadow-sm">
      <button
        type="button"
        disabled={disabled || !!mainAction.disabledReason}
        title={mainAction.disabledReason}
        onClick={mainAction.onClick}
        className={classNames(
          "font-medium whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-50",
          SIZES[size].main,
          hasOtherActions ? "rounded-l-lg" : "rounded-lg",
          MAIN_BUTTON_TONES[tone],
        )}
      >
        {mainAction.label}
      </button>
      {hasOtherActions && (
        <Menu>
          <MenuButton
            disabled={disabled}
            aria-label="Altre azioni"
            className={classNames(
              "group rounded-r-lg transition disabled:cursor-not-allowed disabled:opacity-50",
              SIZES[size].chevron,
              CHEVRON_BUTTON_TONES[tone],
            )}
          >
            <ChevronDown className="w-4 h-4 transition-transform group-data-[open]:rotate-180" />
          </MenuButton>
          <MenuItems
            transition
            anchor="bottom end"
            className="z-50 min-w-48 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 [--anchor-gap:6px] focus:outline-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            {otherActions.map((action) => (
              <MenuItem key={action.label} disabled={!!action.disabledReason}>
                <button
                  type="button"
                  title={action.disabledReason}
                  onClick={action.onClick}
                  className={classNames(
                    "block w-full px-4 py-2 text-left text-sm data-[focus]:bg-gray-50 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40",
                    MENU_ITEM_TONES[action.tone ?? "neutral"],
                  )}
                >
                  {action.label}
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </Menu>
      )}
    </div>
  );
}
